import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useJobs } from '@/hooks/useJobs';
import { useScoreCv, DuplicateInfo } from '@/hooks/useScoreCv';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Sparkles, Upload, X, FileText, CheckCircle2, AlertCircle, Loader2, Trash2, PlusCircle, ShieldCheck, Brain, BarChart3, Check, Lock, ShieldOff, Coins, RotateCcw } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useServiceGovernance } from '@/hooks/useServiceGovernance';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { useQueryClient } from '@tanstack/react-query';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { logConsentAcknowledgement } from '@/lib/consentLogger';
import { buildEmployerCvUploadDescriptor, isExistingStorageObjectError } from '@/lib/forkhire/employerCvUpload';
import { isSupportedPublicCvBrowserFile } from '@/lib/forkhire/publicApplicationCv';
import { ApplicationCvUpdateDialog } from '@/components/cv/ApplicationCvUpdateDialog';
import { HireCvScoringError } from '@/lib/hireCvScoringErrors';
import {
  classifyCvProcessingFailure,
  cvProcessingFailureTranslationBase,
  type CvProcessingFailureKind,
} from '@/lib/forkhire/cvProcessingFailure';

interface CvScoringDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FileWithStatus {
  file: File;
  status: 'pending' | 'uploading' | 'scoring' | 'done' | 'error';
  score?: number;
  error?: string;
  failureKind?: CvProcessingFailureKind;
  retryPending?: boolean;
  applicationId?: string;
  candidateId?: string;
  decision?: 'added' | 'discarded';
}

const MAX_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 2;

const STEPS = [
  { labelKey: 'hire.cvScoring.steps.setup', num: 1 },
  { labelKey: 'hire.cvScoring.steps.processing', num: 2 },
  { labelKey: 'hire.cvScoring.steps.results', num: 3 },
];

const PROCESSING_STAGES = [
  { key: 'upload', labelKey: 'hire.cvScoring.processing.uploading', icon: Upload, subKey: 'hire.cvScoring.processing.uploadingSub' },
  { key: 'extract', labelKey: 'hire.cvScoring.processing.extracting', icon: FileText, subKey: 'hire.cvScoring.processing.extractingSub' },
  { key: 'analyze', labelKey: 'hire.cvScoring.processing.analyzing', icon: Brain, subKey: 'hire.cvScoring.processing.analyzingSub' },
  { key: 'score', labelKey: 'hire.cvScoring.processing.scoring', icon: BarChart3, subKey: 'hire.cvScoring.processing.scoringSub' },
  { key: 'finalize', labelKey: 'hire.cvScoring.processing.finalizing', icon: ShieldCheck, subKey: 'hire.cvScoring.processing.finalizingSub' },
];

const PROCESSING_TIPS = [
  'hire.cvScoring.tips.fit',
  'hire.cvScoring.tips.ranked',
  'hire.cvScoring.tips.breakdown',
  'hire.cvScoring.tips.pdf',
  'hire.cvScoring.tips.secure',
];

function ProcessingOverlay({
  progressPct,
  processedCount,
  totalCount,
  currentFile,
}: {
  progressPct: number;
  processedCount: number;
  totalCount: number;
  currentFile?: FileWithStatus;
}) {
  const { t } = useTranslation();
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTipIdx(prev => (prev + 1) % PROCESSING_TIPS.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  // Determine active stage index based on progress
  const activeStageIdx =
    currentFile?.status === 'uploading' ? 0 :
    currentFile?.status === 'scoring' && progressPct < 40 ? 1 :
    currentFile?.status === 'scoring' && progressPct < 60 ? 2 :
    currentFile?.status === 'scoring' && progressPct < 85 ? 3 :
    4;

  const displayPct = Math.min(
    99,
    Math.round(
      currentFile?.status === 'uploading'
        ? Math.max(progressPct, 10 + (processedCount / totalCount) * 80)
        : currentFile?.status === 'scoring'
        ? Math.max(progressPct, 40 + (processedCount / totalCount) * 50)
        : progressPct
    )
  );

  return (
    <div className="flex flex-col items-center py-6 min-h-[380px]">
      {/* Central animated icon */}
      <motion.div
        className="relative mb-6 w-24 h-24 flex items-center justify-center"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Outer pulsing rings */}
        <motion.div
          className="absolute inset-0 m-auto w-24 h-24 rounded-full border border-primary/10"
          animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute inset-0 m-auto w-24 h-24 rounded-full border-2 border-primary/25 bg-primary/5"
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.1, 0.5] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        />
        {/* Icon container */}
        <div className="relative w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStageIdx}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {(() => {
                const StageIcon = PROCESSING_STAGES[activeStageIdx].icon;
                return <StageIcon className="w-8 h-8 text-primary" />;
              })()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Stage pipeline */}
      <div className="w-full max-w-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          {PROCESSING_STAGES.map((stage, idx) => {
            const isDone = idx < activeStageIdx;
            const isActive = idx === activeStageIdx;
            const StageIcon = stage.icon;

            return (
              <div key={stage.key} className="flex items-center flex-1 last:flex-initial">
                <div className="flex flex-col items-center relative">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                      isDone
                        ? 'bg-primary text-primary-foreground'
                        : isActive
                        ? 'bg-primary/15 text-primary ring-2 ring-primary/30'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isDone ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <StageIcon className={`w-3.5 h-3.5 ${isActive ? 'animate-pulse' : ''}`} />
                    )}
                  </div>
                </div>
                {idx < PROCESSING_STAGES.length - 1 && (
                  <div className="flex-1 mx-1">
                    <div className={`h-0.5 rounded-full transition-all duration-500 ${
                      isDone ? 'bg-primary' : 'bg-border'
                    }`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Active stage label */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStageIdx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="text-center"
          >
            <p className="text-sm font-semibold text-foreground">
              {t(PROCESSING_STAGES[activeStageIdx].labelKey)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t(PROCESSING_STAGES[activeStageIdx].subKey)}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-xs space-y-2 mb-6">
        <Progress value={displayPct} className="h-2.5" />
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {t('hire.cvScoring.processedCount', { processed: processedCount, total: totalCount })}
          </span>
          <span className="font-bold text-primary tabular-nums">{displayPct}%</span>
        </div>
      </div>

      {/* Rotating tips */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tipIdx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
          className="bg-primary/5 border border-primary/10 rounded-xl px-5 py-3 text-xs text-primary max-w-sm text-center"
        >
          {t(PROCESSING_TIPS[tipIdx])}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function CvScoringDialog({ open, onOpenChange }: CvScoringDialogProps) {
  const { t } = useTranslation();
  const { profile, user } = useAuth();
  const { data: jobs } = useJobs();
  const scoreCv = useScoreCv();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cvEntitlement = useEntitlement(SERVICE_KEYS.AI_CV_SCORING);
  const cvGovernance = useServiceGovernance('ai_cv_scoring');

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [discardingIdx, setDiscardingIdx] = useState<number | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<{ fileIdx: number; data: DuplicateInfo; newScore: number } | null>(null);
  const [replacementFileIdx, setReplacementFileIdx] = useState<number | null>(null);

  const activeJobs = (jobs || []).filter(j => j.status === 'active');
  const selectedJobTitle = activeJobs.find(j => j.id === selectedJobId)?.title || '';

  const reset = useCallback(() => {
    setStep(1);
    setSelectedJobId('');
    setFiles([]);
    setIsProcessing(false);
    setProcessedCount(0);
    setDiscardingIdx(null);
    setDuplicateInfo(null);
    setReplacementFileIdx(null);
  }, []);

  const hasUnresolvedResults = step === 3 && files.some(f => f.status === 'done' && !f.decision);

  const handleClose = useCallback((open: boolean) => {
    if (!open) {
      // Block close during active processing
      if (isProcessing) return;
      // If results pending action, just hide — don't reset
      if (hasUnresolvedResults) {
        onOpenChange(false);
        return;
      }
      // Step 1 or all results resolved — full reset
      reset();
    }
    onOpenChange(open);
  }, [isProcessing, hasUnresolvedResults, onOpenChange, reset]);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const incoming = Array.from(newFiles);
    setFiles(prev => {
      const currentCount = prev.length;
      if (currentCount >= MAX_FILES) {
        toast.error(t('hire.cvScoring.errors.maxFiles', { count: MAX_FILES }));
        return prev;
      }
      const available = MAX_FILES - currentCount;
      if (incoming.length > available) {
        toast.error(t('hire.cvScoring.errors.maxFilesAvailable', { count: MAX_FILES, available }));
      }

      const added: FileWithStatus[] = [];
      incoming.slice(0, available).forEach(f => {
        if (!isSupportedPublicCvBrowserFile(f.name, f.type)) {
          toast.error(t('hire.cvScoring.errors.unsupportedFormat', { fileName: f.name }));
          return;
        }
        if (f.size > MAX_SIZE) {
          toast.error(t('hire.cvScoring.errors.tooLarge', { fileName: f.name }));
          return;
        }
        if (prev.some(ef => ef.file.name === f.name && ef.file.size === f.size)) return;
        added.push({ file: f, status: 'pending' });
      });
      return [...prev, ...added];
    });
  }, [t]);

  const removeFile = useCallback((idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleStartScoring = useCallback(async () => {
    if (!selectedJobId) {
      toast.error(t('hire.cvScoring.errors.selectJob'));
      return;
    }
    if (files.length === 0) {
      toast.error(t('hire.cvScoring.errors.uploadOne'));
      return;
    }
    if (!profile?.org_id) return;

    setIsProcessing(true);
    setStep(2);
    setProcessedCount(0);

    // Log employer authorization consent for CV scoring (fire-and-forget)
    logConsentAcknowledgement('cv_scoring', user?.id || null, 'hire', {
      job_id: selectedJobId,
      file_count: files.length,
      consent_categories: ['ai_processing', 'employer_authorization', 'third_party_processing'],
      source_page: '/candidates',
    }).catch(() => {});
    const results = [...files];

    for (let i = 0; i < results.length; i++) {
      const f = results[i];
      let uploadIntentId: string | null = null;
      let createdApplicationId: string | null = null;
      try {
        results[i] = { ...f, status: 'uploading' };
        setFiles([...results]);

        const descriptor = await buildEmployerCvUploadDescriptor(f.file);
        const { data: intent, error: intentError } = await supabase.functions.invoke('cv-upload-intent', {
          body: {
            action: 'create',
            job_id: selectedJobId,
            content_sha256: descriptor.contentSha256,
            extension: descriptor.extension,
            mime_type: descriptor.mimeType,
          },
        });
        if (intentError || !intent?.intent_id || !intent?.storage_path) {
          throw new Error(intent?.error || intentError?.message || t('hire.cvScoring.errors.createApplicationFailed'));
        }
        uploadIntentId = intent.intent_id;
        const storagePath = intent.storage_path;
        const { error: uploadError } = await supabase.storage
          .from('cvs')
          .upload(storagePath, f.file, { contentType: descriptor.mimeType, upsert: false });

        if (uploadError && !isExistingStorageObjectError(uploadError)) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }
        const candidateName = 'cv-upload-pending';

        const submitRes = await supabase.functions.invoke('submit-application', {
          body: {
            slug: activeJobs.find(j => j.id === selectedJobId)?.public_slug,
            fullName: candidateName,
            email: `cv-upload-${crypto.randomUUID().slice(0, 8)}@placeholder.local`,
            cvFilePath: storagePath,
            cvUploadIntentId: uploadIntentId,
            source: 'uploaded',
            skip_auto_score: true,
          },
        });

        if (submitRes.error) {
          const errMsg = typeof submitRes.error === 'object' && 'message' in submitRes.error
            ? submitRes.error.message : String(submitRes.error);
          throw new Error(errMsg || t('hire.cvScoring.errors.createApplicationFailed'));
        }

        const applicationId = submitRes.data?.application_id ?? submitRes.data?.applicationId;
        const candidateId = submitRes.data?.candidate_id ?? submitRes.data?.candidateId;
        if (!applicationId) throw new Error(t('hire.cvScoring.errors.noApplicationId'));
        createdApplicationId = applicationId;

        results[i] = { ...results[i], status: 'scoring', applicationId, candidateId };
        setFiles([...results]);

        const scoreResult = await scoreCv.mutateAsync(applicationId);

        // Check for duplicate detection
        if (scoreResult?.duplicate_found && scoreResult?.duplicate) {
          results[i] = {
            ...results[i],
            status: 'done',
            score: scoreResult?.score ?? scoreResult?.total_score ?? 0,
          };
          setFiles([...results]);
          setProcessedCount(i + 1);
          // Surface duplicate for employer confirmation (handled after loop)
          setDuplicateInfo({ fileIdx: i, data: scoreResult.duplicate, newScore: scoreResult?.score ?? scoreResult?.total_score ?? 0 });
          continue;
        }

        results[i] = {
          ...results[i],
          status: 'done',
          score: scoreResult?.score ?? scoreResult?.total_score ?? 0,
        };
        setFiles([...results]);
        setProcessedCount(i + 1);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('hire.cvScoring.errors.unknown');
        const failureKind = err instanceof HireCvScoringError
          ? classifyCvProcessingFailure({
              code: err.code,
              retryable: err.retryable,
              documentTypeValid: err.documentTypeValid,
            }) ?? undefined
          : undefined;
        results[i] = {
          ...results[i],
          status: 'error',
          error: errorMessage,
          failureKind,
        };
        if (uploadIntentId && !createdApplicationId) {
          await supabase.functions.invoke('cv-upload-intent', {
            body: { action: 'cleanup', intent_id: uploadIntentId },
          }).catch(() => undefined);
        }
        setFiles([...results]);
        setProcessedCount(i + 1);
      }
    }

    setIsProcessing(false);
    setStep(3);
    qc.invalidateQueries({ queryKey: ['candidates'] });
    qc.invalidateQueries({ queryKey: ['job-candidates'] });

    const doneCount = results.filter(r => r.status === 'done').length;
    const errorCount = results.filter(r => r.status === 'error').length;
    toast.success(t('hire.cvScoring.toasts.scoringComplete', { scored: doneCount, failed: errorCount }));
  }, [selectedJobId, files, profile, activeJobs, scoreCv, qc, t, user?.id]);

  const handleRetryProcessing = useCallback(async (idx: number) => {
    const target = files[idx];
    if (!target?.applicationId || target.failureKind !== 'TEMPORARY_RETRY') return;
    setFiles(prev => prev.map((file, i) => i === idx ? { ...file, retryPending: true } : file));
    try {
      const scoreResult = await scoreCv.mutateAsync(target.applicationId);
      setFiles(prev => prev.map((file, i) => i === idx ? {
        ...file,
        status: 'done',
        score: scoreResult?.score ?? scoreResult?.total_score,
        error: undefined,
        failureKind: undefined,
        retryPending: false,
      } : file));
    } catch (error) {
      const failureKind = error instanceof HireCvScoringError
        ? classifyCvProcessingFailure({
            code: error.code,
            retryable: error.retryable,
            documentTypeValid: error.documentTypeValid,
          }) ?? undefined
        : undefined;
      setFiles(prev => prev.map((file, i) => i === idx ? {
        ...file,
        error: error instanceof Error ? error.message : t('hire.cvScoring.errors.unknown'),
        failureKind,
        retryPending: false,
      } : file));
    }
  }, [files, scoreCv, t]);

  const handleAddToJob = useCallback((idx: number) => {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, decision: 'added' } : f));
    toast.success(t('hire.cvScoring.toasts.cvAdded'));
  }, [t]);

  const handleDiscardConfirm = useCallback(async () => {
    if (discardingIdx === null) return;
    const f = files[discardingIdx];
    if (f.applicationId) {
      try {
        const { data, error } = await supabase.functions.invoke('cv-placeholder-lifecycle', {
          body: {
            action: 'discard',
            application_id: f.applicationId,
          },
        });
        if (error || data?.error) {
          throw new Error(error?.message || data?.error || t('hire.cvScoring.errors.discardFailed'));
        }
      } catch (err) {
        console.error('Failed to discard:', err);
        toast.error(err instanceof Error ? err.message : t('hire.cvScoring.errors.discardFailed'));
        setDiscardingIdx(null);
        return;
      }
    }
    setFiles(prev => prev.map((file, i) => i === discardingIdx ? { ...file, decision: 'discarded' } : file));
    setDiscardingIdx(null);
    qc.invalidateQueries({ queryKey: ['candidates'] });
    qc.invalidateQueries({ queryKey: ['job-candidates'] });
    toast.success(t('hire.cvScoring.toasts.cvDiscarded'));
  }, [discardingIdx, files, qc, t]);

  const progressPct = files.length > 0 ? (processedCount / files.length) * 100 : 0;

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold">{t('hire.cvScoring.title')}</DialogTitle>
                <DialogDescription>
                  {step === 1 && t('hire.cvScoring.description.setup')}
                  {step === 2 && selectedJobTitle && t('hire.cvScoring.description.processingFor', { jobTitle: selectedJobTitle })}
                  {step === 3 && selectedJobTitle && t('hire.cvScoring.description.resultsFor', { jobTitle: selectedJobTitle })}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Job title badge (steps 2 & 3) */}
          {step >= 2 && selectedJobTitle && (
            <div className="mt-1">
              <Badge variant="secondary" className="text-xs font-medium">
                📋 {selectedJobTitle}
              </Badge>
            </div>
          )}

          {/* 3-step stepper */}
          <div className="flex items-center gap-1 mt-2">
            {STEPS.map((s, idx) => (
              <div key={s.num} className="flex items-center gap-1 flex-1">
                <div className={`flex items-center gap-1.5 ${step === s.num ? 'text-primary' : step > s.num ? 'text-primary/60' : 'text-muted-foreground'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    step === s.num ? 'bg-primary text-primary-foreground' :
                    step > s.num ? 'bg-primary/20 text-primary' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {step > s.num ? '✓' : s.num}
                  </span>
                  <span className="text-xs font-medium whitespace-nowrap">{t(s.labelKey)}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-1 ${step > s.num ? 'bg-primary/40' : 'bg-border'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Setup & Upload */}
          {step === 1 && (
            <div className="space-y-5 mt-4">
              {/* Governance block — service disabled */}
              {!cvGovernance.allowed && !cvGovernance.isLoading && (
                <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5 text-sm flex items-start gap-3">
                  <ShieldOff className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-destructive">{t('hire.cvScoring.unavailable')}</p>
                    <p className="text-muted-foreground mt-1">{cvGovernance.message}</p>
                  </div>
                </div>
              )}
              {/* Entitlement limit (only if governance allows) */}
              {cvGovernance.allowed && !cvEntitlement.allowed && !cvEntitlement.isLoading && (
                <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>{t('hire.cvScoring.limitReached', { used: cvEntitlement.used, limit: cvEntitlement.limit, credits: cvEntitlement.creditBalance })}</span>
                </div>
              )}
              {cvGovernance.allowed && cvEntitlement.allowed && cvEntitlement.isUsingCredits && (
                <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 text-sm flex items-center gap-2">
                  <Coins className="w-4 h-4 text-primary shrink-0" />
                  <span>{t('hire.cvScoring.creditsNotice', { credits: cvEntitlement.creditBalance })}</span>
                </div>
              )}
              {cvGovernance.allowed && cvEntitlement.isNearLimit && cvEntitlement.allowed && (
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-950/10 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>{t('hire.cvScoring.nearLimit', { percent: cvEntitlement.usagePercent, used: cvEntitlement.used, limit: cvEntitlement.limit })}</span>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-foreground">{t('hire.cvScoring.uploadTitle')}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{t('hire.cvScoring.uploadDescription', { count: MAX_FILES })}</p>

                <div
                  className="mt-3 border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={e => e.preventDefault()}
                >
                  <div className="w-12 h-12 mx-auto rounded-xl bg-muted/60 flex items-center justify-center mb-3">
                    <Upload className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="font-semibold text-foreground">{t('hire.cvScoring.dropzoneTitle')}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t('hire.cvScoring.dropzoneDescription', { count: MAX_FILES })}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>

                {files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files.map((f, i) => (
                      <div key={`${f.file.name}-${i}`} className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-lg text-sm">
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="truncate flex-1 text-foreground">{f.file.name}</span>
                        <span className="text-xs text-muted-foreground">{(f.file.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-foreground">{t('hire.cvScoring.selectJobTitle')}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{t('hire.cvScoring.selectJobDescription')}</p>
                <div className="mt-2">
                  <label className="text-sm font-medium text-foreground">{t('hire.cvScoring.jobPosition')}</label>
                  <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder={t('hire.cvScoring.selectJobPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeJobs.map(j => (
                        <SelectItem key={j.id} value={j.id}>
                          {j.title}{j.department ? ` — ${j.department}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-accent/5 border border-accent/20 rounded-xl p-4">
                <div className="flex gap-2">
                  <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-accent text-sm">{t('hire.cvScoring.analysisTitle')}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {t('hire.cvScoring.analysisDescription')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <div className="flex gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-primary text-sm">{t('hire.cvScoring.authorizationTitle')}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {t('hire.cvScoring.authorizationDescription')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => handleClose(false)} className="rounded-xl">{t('common.cancel')}</Button>
                <Button
                  onClick={handleStartScoring}
                  disabled={files.length === 0 || !selectedJobId || !cvGovernance.allowed || cvEntitlement.isLoading || !cvEntitlement.allowed || isProcessing}
                  className="rounded-xl gap-1.5"
                >
                  <Sparkles className="w-4 h-4" /> {t('hire.cvScoring.startScoring')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Processing */}
          {step === 2 && (
            <ProcessingOverlay
              progressPct={progressPct}
              processedCount={processedCount}
              totalCount={files.length}
              currentFile={files.find(f => f.status === 'uploading' || f.status === 'scoring')}
            />
          )}

          {/* Step 3: Results */}
          {step === 3 && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {files.map((f, i) => {
                  const failureBase = f.failureKind ? cvProcessingFailureTranslationBase(f.failureKind) : null;
                  return (
                  <div
                    key={`${f.file.name}-${i}`}
                    className={`flex items-center gap-3 px-4 py-3 border rounded-xl transition-colors ${
                      f.decision === 'added' ? 'bg-success/5 border-success/30' :
                      f.decision === 'discarded' ? 'bg-muted/20 border-border opacity-50' :
                      'bg-muted/30 border-border'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {f.status === 'done' && !f.decision && <CheckCircle2 className="w-5 h-5 text-success" />}
                      {f.decision === 'added' && <PlusCircle className="w-5 h-5 text-success" />}
                      {f.decision === 'discarded' && <Trash2 className="w-5 h-5 text-muted-foreground" />}
                      {f.status === 'error' && <AlertCircle className="w-5 h-5 text-destructive" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{f.file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {f.decision === 'added' && t('hire.cvScoring.results.addedToJob')}
                        {f.decision === 'discarded' && t('hire.cvScoring.results.discarded')}
                        {!f.decision && f.status === 'done' && t('hire.cvScoring.results.score', { score: f.score })}
                        {f.status === 'error' && (failureBase ? t(`${failureBase}.title`) : (f.error || t('hire.cvScoring.results.failed')))}
                      </p>
                      {f.status === 'error' && failureBase && (
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t(`${failureBase}.description`)}</p>
                      )}
                    </div>
                    {f.status === 'done' && f.score != null && (
                      <div className={`text-sm font-bold px-2.5 py-1 rounded-lg ${
                        f.score >= 80 ? 'bg-success/10 text-success' :
                        f.score >= 60 ? 'bg-amber-500/10 text-amber-600' :
                        'bg-rose-500/10 text-rose-600'
                      }`}>
                        {f.score}
                      </div>
                    )}
                    {f.status === 'done' && !f.decision && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg h-8 text-xs gap-1 border-success/30 text-success hover:bg-success/10"
                          onClick={() => handleAddToJob(i)}
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> {t('common.add')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg h-8 text-xs gap-1 border-destructive/30 text-destructive hover:bg-destructive/10"
                          onClick={() => setDiscardingIdx(i)}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> {t('hire.cvScoring.actions.discard')}
                        </Button>
                      </div>
                    )}
                    {f.status === 'error' && f.failureKind === 'TEMPORARY_RETRY' && f.applicationId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 shrink-0 gap-1 text-xs"
                        disabled={f.retryPending || scoreCv.isPending}
                        onClick={() => handleRetryProcessing(i)}
                      >
                        {f.retryPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        {t(`${failureBase}.action`)}
                      </Button>
                    )}
                    {f.status === 'error' && (f.failureKind === 'REPLACEMENT_REQUIRED_UNREADABLE' || f.failureKind === 'REPLACEMENT_REQUIRED_NOT_CV') && f.applicationId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 shrink-0 gap-1 text-xs"
                        onClick={() => setReplacementFileIdx(i)}
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {t(`${failureBase}.action`)}
                      </Button>
                    )}
                  </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={reset} className="rounded-xl">{t('hire.cvScoring.actions.scoreMore')}</Button>
                <Button onClick={() => handleClose(false)} className="rounded-xl">{t('common.done')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Discard Confirmation */}
      <AlertDialog open={discardingIdx !== null} onOpenChange={open => { if (!open) setDiscardingIdx(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('hire.cvScoring.discardDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('hire.cvScoring.discardDialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('hire.cvScoring.discardDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate application notice */}
      <AlertDialog open={duplicateInfo !== null} onOpenChange={open => { if (!open) setDuplicateInfo(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('hire.cvScoring.duplicateDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {t('hire.cvScoring.duplicateDialog.description')}
                </p>
                {duplicateInfo && (
                  <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('hire.cvScoring.duplicateDialog.existing')}</span>
                      <span className="font-medium text-foreground">{duplicateInfo.data.existing_candidate_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('hire.cvScoring.duplicateDialog.email')}</span>
                      <span className="font-medium text-foreground">{duplicateInfo.data.existing_candidate_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('hire.cvScoring.duplicateDialog.currentStage')}</span>
                      <Badge variant="secondary" className="capitalize text-xs">{duplicateInfo.data.existing_application_stage}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('hire.cvScoring.duplicateDialog.previousScore')}</span>
                      <span className="font-medium text-foreground">
                        {duplicateInfo.data.existing_cv_score !== null ? `${duplicateInfo.data.existing_cv_score}/100` : t('hire.cvScoring.duplicateDialog.notScored')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('hire.cvScoring.duplicateDialog.newScore')}</span>
                      <span className="font-medium text-primary">
                        {duplicateInfo.newScore}/100
                      </span>
                    </div>
                    <div className="border-t my-2" />
                    <p className="text-muted-foreground">
                      {t('hire.cvScoring.duplicateDialog.mergeNotice')}
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setDuplicateInfo(null)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('common.close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {replacementFileIdx !== null && files[replacementFileIdx]?.applicationId && (
        <ApplicationCvUpdateDialog
          open
          onOpenChange={(open) => { if (!open) setReplacementFileIdx(null); }}
          jobId={selectedJobId}
          applicationId={files[replacementFileIdx].applicationId!}
          onSuccess={(result) => {
            setFiles(prev => prev.map((file, i) => i === replacementFileIdx ? {
              ...file,
              status: 'done',
              score: result.score,
              error: undefined,
              failureKind: undefined,
            } : file));
          }}
        />
      )}
    </>
  );
}
